import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { isMobile, isSafari, isChrome } from 'react-device-detect';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';

import Button from 'common/components/Mui/Button';

const shouldManuallyCheckFileTypes = isMobile && !isSafari && !isChrome;
const acceptableExtensions = [
    'csv',
    'xls',
    'xlsx',
    '7z',
    'gzip',
    'rar',
    'zip',
    'doc',
    'docx',
    'pdf',
    'ppt',
    'pptx',
    'txt'
];
const acceptableExtensionsStr = acceptableExtensions.map(e => `.${e}`).join(',');
const acceptableExtensionsRegExp = new RegExp(acceptableExtensions.join('|'), 'i');
const accept = `image/*,${acceptableExtensionsStr}`;

function checkIsAcceptableFile(file = {}) {
    const { name, type } = file;
    return /^image\//i.test(type) || acceptableExtensionsRegExp.test(name);
}

export default function FileUpload({
    style,
    disabled,
    onChange = () => {},
    onError = () => {},
    onClear = () => {},
    value = null
}) {
    const { t } = useTranslation();
    const { getRootProps, getInputProps } = useDropzone({
        onDropAccepted: ([file]) => {
            if (shouldManuallyCheckFileTypes) {
                if (checkIsAcceptableFile(file)) {
                    onChange(file);
                } else {
                    onError(file);
                }
            } else {
                onChange(file);
            }
        },
        onDropRejected: ([file]) => onError(file),
        multiple: false,
        maxSize: 10000000,
        ...(shouldManuallyCheckFileTypes ? {} : { accept })
    });
    const filename = value instanceof File ? value.name : value;
    return (
        <Fragment>
            {value ? (
                <div css={css(style)}>
                    {filename}
                    <IconButton
                        onClick={() => {
                            onClear();
                        }}
                        css={clearBtnStyle}
                        size="small"
                    >
                        <ClearIcon />
                    </IconButton>
                </div>
            ) : (
                <span {...getRootProps()}>
                    <Button variant="contained" css={[uploadBtnStyle(style)]} disabled={disabled}>
                        {t('section.answer.fileUpload.placeholder')}
                    </Button>
                </span>
            )}
            <input {...getInputProps()} disabled={disabled} />
        </Fragment>
    );
}

FileUpload.propTypes = {
    disabled: PropTypes.bool,
    onError: PropTypes.func,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    style: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

const uploadBtnStyle = style => ({
    ...style,
    '&.Mui-disabled': {
        color: style.color,
        opacity: 0.5
    }
});

const clearBtnStyle = css`
    margin-left: 5px;
`;
